import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Clock, PanelLeftClose, Plus } from '@/ui/icons'
import { useTrips } from '@/modules/trips'
import { useSubscription } from '@/modules/subscriptions/hooks/useSubscription'
import type { RangerSessionSummary } from '@/shared/api/ranger'
import { cn } from '@/shared/utils'
import { useRanger } from '../hooks/useRanger'
import { useRangerSessions } from '../hooks/useRangerSessions'
import { RangerChat } from './RangerChat'
import { RangerHistory } from './RangerHistory'
import { RangerMark } from './RangerMark'

const ICON_BUTTON_CLASS =
  'inline-flex h-8 w-8 items-center justify-center rounded-md text-earth-600 transition-colors hover:bg-earth-100 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500'

interface RangerColumnProps {
  tripId?: string
  open: boolean
  onOpen: () => void
  onClose: () => void
}

/**
 * Ranger as a permanent desktop column, between the app navbar and the trips list.
 * The conversation stays mounted while collapsed so it survives closing and reopening,
 * and moving between trips.
 */
export function RangerColumn({ tripId, open, onOpen, onClose }: RangerColumnProps) {
  const navigate = useNavigate()
  const { trips } = useTrips()
  const ranger = useRanger(tripId)
  const { builtTrip, activeTool, messages, loading } = ranger
  const { status } = useSubscription()
  const canUseRanger = status?.features.canUseRanger ?? false

  // Only a deliberate open takes focus. The automatic one must not pull keyboard focus on page load.
  const [focusRequest, setFocusRequest] = useState(0)
  const handleOpen = () => {
    onOpen()
    setFocusRequest((n) => n + 1)
  }

  // Show the new trip as soon as it exists. The chat stays put for the invite step.
  const navigatedTripIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!builtTrip || navigatedTripIdRef.current === builtTrip.id) return
    navigatedTripIdRef.current = builtTrip.id
    navigate(`/trips/${builtTrip.id}`)
  }, [builtTrip, navigate])

  const [showHistory, setShowHistory] = useState(false)
  const [openingSessionId, setOpeningSessionId] = useState<string | null>(null)
  const [openFailed, setOpenFailed] = useState(false)
  // A chat older than the recent list still needs its name in the header once it's open.
  const [openedSession, setOpenedSession] = useState<{ runtimeSessionId: string; title: string | null } | null>(null)

  const closeHistory = () => {
    setShowHistory(false)
    setOpenFailed(false)
    setFocusRequest((n) => n + 1)
  }

  const startNewChat = () => {
    ranger.clearMessages()
    setOpeningSessionId(null)
    closeHistory()
  }

  const openSession = async (session: RangerSessionSummary) => {
    if (session.runtimeSessionId === ranger.sessionId) {
      closeHistory()
      return
    }

    setOpeningSessionId(session.id)
    setOpenFailed(false)
    try {
      const transcript = await ranger.openSession(session.id)
      if (!transcript) return

      setOpenedSession({ runtimeSessionId: transcript.runtimeSessionId, title: transcript.title })
      // Ranger acts on the open trip, so a chat about another trip has to bring that trip with it.
      // The trips list guards against a trip the user has since lost access to.
      if (transcript.tripId && transcript.tripId !== tripId && trips?.some((t) => t.id === transcript.tripId)) {
        navigate(`/trips/${transcript.tripId}`)
      }
      setOpeningSessionId(null)
      closeHistory()
    } catch {
      setOpeningSessionId(null)
      setOpenFailed(true)
    }
  }

  const handleRenamed = (session: RangerSessionSummary, title: string) => {
    if (openedSession?.runtimeSessionId === session.runtimeSessionId) {
      setOpenedSession({ runtimeSessionId: session.runtimeSessionId, title })
    }
  }

  const handleDeleted = (session: RangerSessionSummary) => {
    if (session.runtimeSessionId === ranger.sessionId) ranger.clearMessages()
  }

  // The list is the source of a chat's name, including one Ranger just named after its first reply.
  const recentSessions = useRangerSessions('recent', { enabled: open && canUseRanger })
  const chatName =
    recentSessions.data?.pages[0]?.sessions.find((s) => s.runtimeSessionId === ranger.sessionId)?.title ??
    (openedSession?.runtimeSessionId === ranger.sessionId ? openedSession.title : null)

  const chatTitle =
    activeTool === 'build_trip' ? 'Build a trip' : chatName || (messages.length > 0 ? 'Ranger' : 'New chat')
  const tripName = tripId ? trips?.find((t) => t.id === tripId)?.name : undefined

  return (
    <aside
      aria-label="Ranger AI Assistant"
      className={cn(
        'hidden h-full flex-shrink-0 overflow-hidden border-r border-earth-200 bg-background transition-[width] duration-200 ease-out motion-reduce:transition-none md:flex',
        open ? 'w-[340px] 2xl:w-[380px]' : 'w-12'
      )}
    >
      {!open && (
        <div className="flex w-12 flex-shrink-0 flex-col items-center py-3">
          <button
            type="button"
            onClick={handleOpen}
            aria-label="Open Ranger AI Assistant"
            title="Open Ranger"
            className={ICON_BUTTON_CLASS}
          >
            <RangerMark thinking={loading} className="h-5 w-5 text-adventure-600" />
          </button>
        </div>
      )}

      <div className={cn('h-full w-[340px] flex-shrink-0 flex-col 2xl:w-[380px]', open ? 'flex' : 'hidden')}>
        <div className={cn('flex h-14 flex-shrink-0 items-center justify-between gap-2 pr-2', showHistory ? 'pl-2' : 'pl-4')}>
          <div className="flex min-w-0 items-center gap-1">
            {showHistory && (
              <button
                type="button"
                onClick={closeHistory}
                aria-label="Back to chat"
                title="Back to chat"
                className={ICON_BUTTON_CLASS}
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <h2 className="truncate text-sm font-medium text-foreground" title={showHistory ? undefined : chatTitle}>
              {showHistory ? 'Chats' : chatTitle}
            </h2>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            {canUseRanger && !showHistory && (
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                aria-label="Chat history"
                title="Chat history"
                className={ICON_BUTTON_CLASS}
              >
                <Clock className="h-4 w-4" />
              </button>
            )}
            {(messages.length > 0 || showHistory) && (
              <button
                type="button"
                onClick={startNewChat}
                aria-label="Start a new chat"
                title="New chat"
                className={ICON_BUTTON_CLASS}
              >
                <Plus className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Collapse Ranger AI Assistant"
              title="Collapse"
              className={ICON_BUTTON_CLASS}
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="relative min-h-0 flex-1">
          {/* History sits over the chat, not in place of it, so a half-typed message and the
              scroll position are still there on the way back. */}
          <div className="h-full" {...(showHistory ? { inert: '' } : {})}>
            <RangerChat
              tripId={tripId}
              tripName={tripName}
              messages={messages}
              sendMessage={ranger.sendMessage}
              loading={ranger.loading}
              error={ranger.error}
              clearError={ranger.clearError}
              tools={{ activeTool, tripBuilt: builtTrip !== null, onStart: ranger.startTool, onStop: ranger.stopTool }}
              autoFocus={false}
              focusRequest={focusRequest}
            />
          </div>
          {showHistory && (
            <RangerHistory
              activeRuntimeSessionId={ranger.sessionId}
              openingSessionId={openingSessionId}
              openFailed={openFailed}
              onOpen={openSession}
              onRenamed={handleRenamed}
              onDeleted={handleDeleted}
              onClose={closeHistory}
            />
          )}
        </div>
      </div>
    </aside>
  )
}
